<template>
	<form class="relative flex flex-col">
		<AuthForm
			v-if="props.page === 'login-1'"
			:data="state.formData"
			@change="(v) => (state.formData = v)"
			:show-mobile="props.mode == 'mobile'"
			:show-email="props.mode == 'email'"
			:title="$t('login')"
			:subtitle="$t('are_you_a_new_user')"
			:subtitle-link="$t('create_an_account')"
			@click-subtitle-link="changePage('register')"
			:bottom-link="$t('forgot_password')"
			@click-bottom-link="changePage('forget-password')"
			:submit-title="$t('login')"
			@submit="submit"
			:wrong-count="state.wrongCount"
			class="flex-grow"
			:mode="props.mode"
			show-mode-selector
			@update:mode="(v) => emit('update:mode', v)"
		/>
		<AuthForm
			v-if="props.page === 'login-2'"
			:data="state.formData"
			@change="(v) => (state.formData = v)"
			:show-password="true"
			:show-captcha="true"
			:show-edit-mobile="props.mode == 'mobile'"
			:show-edit-email="props.mode == 'email'"
			@edit-mobile="changePage('login-1')"
			@edit-email="changePage('login-1')"
			:title="$t('login')"
			show-checkbox
			:subtitle="$t('are_you_a_new_user')"
			:subtitle-link="$t('create_an_account')"
			@click-subtitle-link="changePage('register')"
			:bottom-link="$t('forgot_password')"
			@click-bottom-link="changePage('forget-password')"
			:submit-title="$t('login')"
			@submit="submit"
			:wrong-count="state.wrongCount"
			class="flex-grow"
			:mode="props.mode"
			@update:mode="(v) => emit('update:mode', v)"
		/>
		<AuthForm
			v-if="props.page === 'register'"
			:data="state.formData"
			@change="(v) => (state.formData = v)"
			:show-mobile="props.mode == 'mobile'"
			:show-email="props.mode == 'email'"
			show-firstname
			show-lastname
			show-address
			:show-password="true"
			:show-re-password="true"
			:show-captcha="true"
			show-checkbox
			:title="$t('register')"
			:subtitle="$t('already_created_an_account')"
			:subtitle-link="$t('login')"
			@click-subtitle-link="changePage('login-1')"
			:submit-title="$t('register')"
			@submit="submit"
			:wrong-count="state.wrongCount"
			class="flex-grow"
			:mode="props.mode"
			show-mode-selector
			@update:mode="(v) => emit('update:mode', v)"
		/>
		<AuthForm
			v-if="props.page === 'verify-user'"
			:data="state.formData"
			@change="(v) => (state.formData = v)"
			:show-edit-mobile="props.mode == 'mobile'"
			:show-edit-email="props.mode == 'email'"
			@edit-mobile="changePage('login-1')"
			@edit-email="changePage('login-1')"
			:show-code="true"
			:title="$t('activation_code')"
			:subtitle="$t('activation_code_sent_to_n', { name: authMode?.title })"
			:submit-title="$t('confirm')"
			@submit="submit"
			:show-back="!hideBack"
			@back="changePage('login-1')"
			:wrong-count="state.wrongCount"
			class="flex-grow"
			:mode="props.mode"
			@update:mode="(v) => emit('update:mode', v)"
		/>
		<AuthDone
			v-if="props.page === 'activation-done'"
			:title="$t('successful_account_activation')"
			:btn-title="$t('login')"
			@submit="submit"
			class="flex-grow"
		/>
		<AuthForm
			v-if="props.page === 'forget-password'"
			:data="state.formData"
			@change="(v) => (state.formData = v)"
			:show-mobile="mode == 'mobile'"
			:show-email="mode == 'email'"
			:show-captcha="true"
			:title="$t('forgot_password')"
			:subtitle="$t('forget_password_subtitle_n', { name: authMode?.title })"
			:submit-title="$t('send')"
			@submit="submit"
			:show-back="!hideBack"
			@back="changePage('login-1')"
			:wrong-count="state.wrongCount"
			class="flex-grow"
			:mode="props.mode"
			show-mode-selector
			@update:mode="(v) => emit('update:mode', v)"
		/>
		<AuthForm
			v-if="props.page === 'verify-forget'"
			:data="state.formData"
			@change="(v) => (state.formData = v)"
			:show-edit-mobile="mode == 'mobile'"
			:show-edit-email="mode == 'email'"
			@edit-mobile="changePage('forget-password')"
			@edit-email="changePage('forget-password')"
			:show-code="true"
			:title="$t('confirmation_code')"
			:subtitle="$t('confirmation_code_sent')"
			:submit-title="$t('confirm')"
			@submit="submit"
			:show-back="!hideBack"
			@back="changePage('login-1')"
			:wrong-count="state.wrongCount"
			class="flex-grow"
			:mode="props.mode"
			@update:mode="(v) => emit('update:mode', v)"
		/>
		<AuthForm
			v-if="props.page === 'change-password'"
			:data="state.formData"
			@change="(v) => (state.formData = v)"
			:show-password="true"
			:show-re-password="true"
			:title="$t('change_password')"
			:subtitle="$t('enter_your_new_password')"
			:submit-title="$t('change')"
			@submit="submit"
			:wrong-count="state.wrongCount"
			class="flex-grow"
			:mode="props.mode"
			@update:mode="(v) => emit('update:mode', v)"
		/>
		<AuthDone
			v-if="props.page === 'change-password-done'"
			:title="$t('successful_password_change')"
			:btn-title="$t('login')"
			@submit="submit"
			class="flex-grow"
		/>
	</form>
</template>

<script setup lang="ts">
import { UserAuthMode } from "~/stores/user"

const app = useNuxtApp()
const userStore = useUserStore()

type PageIds =
	| "login-1"
	| "login-2"
	| "register"
	| "forget-password"
	| "change-password"
	| "verify-forget"
	| "verify-user"
	| "activation-done"
	| "change-password-done"

const emit = defineEmits<{
	"update:loading": [boolean]
	"update:page": [PageIds]
	"update:mode": [UserAuthMode["id"]]
	close: []
	done: []
}>()

const props = withDefaults(
	defineProps<{
		loading?: boolean
		page: PageIds
		hideBack?: boolean
		mode?: UserAuthMode["id"]
	}>(),
	{
		mode: "email"
	}
)

const state = reactive({
	formData: {
		mobile: "+1"
	} as any,
	wrongCount: 1,
	loading: false
})

const authMode = computed(() => userStore.authModes.find((v) => v.id == props.mode))

const changePage = (page: PageIds) => {
	emit("update:page", page)
}

const submit = () => {
	if (props.page === "login-1") {
		check()
	} else if (props.page === "login-2") {
		login()
	} else if (props.page === "register") {
		register()
	} else if (props.page === "forget-password") {
		forgetPassword()
	} else if (props.page === "change-password") {
		changePassword()
	} else if (props.page === "verify-forget") {
		verifyForget()
	} else if (props.page === "verify-user" && props.mode == "mobile") {
		verifyMobile()
	} else if (props.page === "verify-user" && props.mode == "email") {
		verifyEmail()
	} else if (props.page === "activation-done") {
		changePage("login-1")
	} else if (props.page === "change-password-done") {
		changePage("login-1")
	}
}

const check = async () => {
	const errors = []
	if (props.mode == "mobile") {
		errors.push(...app.$validator.validate(state.formData.mobile, app.$i18n.t("phone_number"), "valid|mobile"))
	}
	if (props.mode == "email") {
		errors.push(...app.$validator.validate(state.formData.email, app.$i18n.t("email"), "valid|email"))
	}
	if (errors.length) {
		app.$toast.error({ message: errors[0] })
		return
	}

	if (state.loading) return
	state.loading = true

	try {
		const r = await userStore.check({
			mobile: (props.mode == "mobile" && state.formData.mobile) || undefined,
			email: (props.mode == "email" && state.formData.email) || undefined
		})
		if (!r.status) {
			changePage("register")
		} else {
			changePage("login-2")
		}
	} catch (e: any) {
		app.$toastErrors(e)
		state.wrongCount++
	}

	state.loading = false
}

const login = async () => {
	const errors = [
		...app.$validator.validate(state.formData.password, app.$i18n.t("password"), "valid")
		// ...app.$validator.validate(state.formData.captcha, app.$i18n.t("captcha"), "valid")
	]
	if (props.mode == "mobile") {
		errors.push(...app.$validator.validate(state.formData.mobile, app.$i18n.t("phone_number"), "valid|mobile"))
	}
	if (props.mode == "email") {
		errors.push(...app.$validator.validate(state.formData.email, app.$i18n.t("email"), "valid|email"))
	}
	if (errors.length) {
		app.$toast.error({ message: errors[0] })
		return
	}

	if (state.loading) return
	state.loading = true

	try {
		const r = await userStore.login({
			mobile: (props.mode == "mobile" && state.formData.mobile) || undefined,
			email: (props.mode == "email" && state.formData.email) || undefined,
			password: state.formData.password,
			captcha_key: state.formData.captcha_key,
			captcha: state.formData.captcha,
			locale: app.$i18n.localeProperties.value.code
		})
		if (r.status == "disable") {
			app.$toast.error({ message: app.$i18n.t("account_not_active") })
			app.$toast.info({ message: app.$i18n.t("activation_code_sent_to_n", { name: authMode.value?.title }) })
			state.formData.code = r.code || null
			changePage("verify-user")
		} else {
			app.$toast.success({ message: app.$i18n.t("logged_in") })
			app.$router.push(app.$routeUrl.HomeUrl())
			emit("close")
		}
	} catch (e: any) {
		app.$toastErrors(e)
		state.wrongCount++
	}

	state.loading = false
}

const register = async () => {
	const errors = [
		...app.$validator.validate(state.formData.password, app.$i18n.t("password"), "valid"),
		...app.$validator.validate(
			state.formData.password,
			app.$i18n.t("repeat_password"),
			`valid|equel[${state.formData.re_password}]`
		),
		...app.$validator.validate(state.formData.firstname, app.$i18n.t("firstname"), "valid"),
		...app.$validator.validate(state.formData.lastname, app.$i18n.t("lastname"), "valid"),
		...app.$validator.validate(state.formData.longitude && state.formData.latitude, app.$i18n.t("address"), "valid")
		// ...app.$validator.validate(state.formData.captcha, app.$i18n.t("captcha"), "valid")
	]
	if (props.mode == "mobile") {
		errors.push(...app.$validator.validate(state.formData.mobile, app.$i18n.t("phone_number"), "valid|mobile"))
	}
	if (props.mode == "email") {
		errors.push(...app.$validator.validate(state.formData.email, app.$i18n.t("email"), "valid|email"))
	}
	if (errors.length) {
		app.$toast.error({ message: errors[0] })
		return
	}

	if (props.loading) return
	emit("update:loading", true)

	try {
		const r = await userStore.register({
			mobile: (props.mode == "mobile" && state.formData.mobile) || undefined,
			email: (props.mode == "email" && state.formData.email) || undefined,
			firstname: state.formData.firstname,
			lastname: state.formData.lastname,
			longitude: state.formData.longitude,
			latitude: state.formData.latitude,
			password: state.formData.password,
			captcha_key: state.formData.captcha_key,
			captcha: state.formData.captcha
		})

		app.$toast.success({ message: app.$i18n.t("registered") })
		app.$toast.info({ message: app.$i18n.t("activation_code_sent") })
		state.formData.code = r.code || null
		changePage("verify-user")
	} catch (e: any) {
		app.$toastErrors(e)
		state.wrongCount++
	}

	emit("update:loading", false)
}

const verifyMobile = async () => {
	const errors = [...app.$validator.validate(state.formData.code, app.$i18n.t("code"), "valid")]
	if (errors.length) {
		app.$toast.error({ message: errors[0] })
		return
	}

	if (props.loading) return
	emit("update:loading", true)

	try {
		await userStore.verifyMobile({
			mobile: state.formData.mobile,
			code: state.formData.code
		})
		changePage("activation-done")
	} catch (e: any) {
		app.$toastErrors(e)
		state.wrongCount++
	}

	emit("update:loading", false)
}

const verifyEmail = async () => {
	const errors = [...app.$validator.validate(state.formData.code, app.$i18n.t("code"), "valid")]
	if (errors.length) {
		app.$toast.error({ message: errors[0] })
		return
	}

	if (props.loading) return
	emit("update:loading", true)

	try {
		await userStore.verifyEmail({
			email: state.formData.email,
			code: state.formData.code
		})
		changePage("activation-done")
	} catch (e: any) {
		app.$toastErrors(e)
		state.wrongCount++
	}

	emit("update:loading", false)
}

const verifyForget = async () => {
	const errors = [
		// ...app.$validator.validate(state.formData.captcha, app.$i18n.t("captcha"), "valid")
	]
	if (props.mode == "mobile") {
		errors.push(...app.$validator.validate(state.formData.mobile, app.$i18n.t("phone_number"), "valid|mobile"))
	}
	if (props.mode == "email") {
		errors.push(...app.$validator.validate(state.formData.email, app.$i18n.t("email"), "valid|email"))
	}
	if (errors.length) {
		app.$toast.error({ message: errors[0] })
		return
	}

	if (props.loading) return
	emit("update:loading", true)

	try {
		const r = await userStore.verifyCode({
			mobile: (props.mode == "mobile" && state.formData.mobile) || undefined,
			email: (props.mode == "email" && state.formData.email) || undefined,
			code: state.formData.code
		})
		state.formData.code = r.code || null
		changePage("change-password")
	} catch (e: any) {
		app.$toastErrors(e)
		state.wrongCount++
	}

	emit("update:loading", false)
}

const forgetPassword = async () => {
	const errors = [
		// ...app.$validator.validate(state.formData.captcha, app.$i18n.t("captcha"), "valid")
	]
	if (props.mode == "mobile") {
		errors.push(...app.$validator.validate(state.formData.mobile, app.$i18n.t("phone_number"), "valid|mobile"))
	}
	if (props.mode == "email") {
		errors.push(...app.$validator.validate(state.formData.email, app.$i18n.t("email"), "valid|email"))
	}
	if (errors.length) {
		app.$toast.error({ message: errors[0] })
		return
	}

	if (props.loading) return
	emit("update:loading", true)

	try {
		const r = await userStore.requestForget({
			mobile: (props.mode == "mobile" && state.formData.mobile) || undefined,
			email: (props.mode == "email" && state.formData.email) || undefined,
			captcha_key: state.formData.captcha_key,
			captcha: state.formData.captcha
		})
		app.$toast.info({ message: app.$i18n.t("confirmation_code_sent") })
		state.formData.code = r.code || null
		changePage("verify-forget")
	} catch (e: any) {
		app.$toastErrors(e)
		state.wrongCount++
	}

	emit("update:loading", false)
}

const changePassword = async () => {
	const errors = [
		...app.$validator.validate(state.formData.password, app.$i18n.t("password"), "valid"),
		...app.$validator.validate(
			state.formData.password,
			app.$i18n.t("repeat_password"),
			`valid|equel[${state.formData.re_password}]`
		)
	]
	if (props.mode == "mobile") {
		errors.push(...app.$validator.validate(state.formData.mobile, app.$i18n.t("phone_number"), "valid|mobile"))
	}
	if (props.mode == "email") {
		errors.push(...app.$validator.validate(state.formData.email, app.$i18n.t("email"), "valid|email"))
	}
	if (errors.length) {
		app.$toast.error({ message: errors[0] })
		return
	}

	if (props.loading) return
	emit("update:loading", true)

	try {
		const r = await userStore.changePassword({
			mobile: (props.mode == "mobile" && state.formData.mobile) || undefined,
			email: (props.mode == "email" && state.formData.email) || undefined,
			password: state.formData.password,
			code: state.formData.code
		})
		changePage("change-password-done")
	} catch (e: any) {
		app.$toastErrors(e)
		state.wrongCount++
	}

	emit("update:loading", false)
}

watch(
	() => props.page,
	() => state.wrongCount++
)

onMounted(() => {})
</script>
